import React, { useState, useEffect, useMemo } from 'react';
import { fmtDate, fmtDateTime } from '@shared/formatDate';
import { useQuery } from '@tanstack/react-query';
import {
  Container,
  Box,
  Tabs,
  Tab,
  Typography,
  Alert,
  CircularProgress,
  Button,
  Paper,
  Drawer,
  IconButton,
  Chip,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Stack,
  Collapse,
} from '@mui/material';
import { 
  Settings as SettingsIcon, 
  Close as CloseIcon,
  ContentCopy as ContentCopyIcon,
  CheckCircle as CheckCircleIcon,
  People as PeopleIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  FamilyRestroom as FamilyIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import ProfileView from '../components/profile/ProfileView';
import EnhancedProfileEditor from '../components/profile/EnhancedProfileEditor';
import AliasManager from '../components/profile/AliasManager';
import ChangePassword from '../components/profile/ChangePassword';
import AccountSettings from '../components/profile/AccountSettings';
import MinorAccessManager from '../components/profile/MinorAccessManager';
import AdditionalMembersManager from '../components/profile/AdditionalMembersManager';
import { useAuth } from '../hooks/useAuth';
import { getVisibleTabKeys } from '../services/configService';
import socialService, { ProfileDto, ProfileAliasDto } from '../services/socialService';
import jointAccountService, { JointAccountAlias } from '../services/jointAccountService';
import userAccountService, { 
  AliasAccountDetails,
  ReferralLinksResponse, 
  ReferralSchedule, 
  ReferredUser 
} from '../services/userAccountService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const MyProfile: React.FC = () => {
  const { isBusinessAccount, hasAdvancedMailbox, isJointContext, jointAccountName } = useAuth();
  const [resolvedIsBusinessAccount, setResolvedIsBusinessAccount] = useState(isBusinessAccount);
  const { data: visibleTabKeys = [] } = useQuery({
    queryKey: ['visibleTabKeys'],
    queryFn: getVisibleTabKeys,
    staleTime: 5 * 60 * 1000,
    enabled: true,
  });
  const [currentTab, setCurrentTab] = useState(0);

  const profileTabKeys = useMemo(() => [
    'profile/your-profiles',
    'profile/account-settings',
    resolvedIsBusinessAccount ? 'profile/additional-members' : 'profile/minor-access',
  ], [resolvedIsBusinessAccount]);

  const visibleTabIndices = useMemo(() => {
    const visible = (key: string) => hasAdvancedMailbox || (visibleTabKeys.length === 0 || visibleTabKeys.includes(key));
    return profileTabKeys.map((key, i) => visible(key) ? i : -1).filter((i) => i >= 0);
  }, [hasAdvancedMailbox, visibleTabKeys, profileTabKeys]);

  const realTabIndex = visibleTabIndices[currentTab] ?? visibleTabIndices[0] ?? 0;
  const tabLabels = ['Your Profiles', 'Account Settings', resolvedIsBusinessAccount ? 'Additional Members' : 'Minor Access'];

  useEffect(() => {
    if (visibleTabIndices.length > 0 && currentTab >= visibleTabIndices.length) setCurrentTab(0);
  }, [visibleTabIndices.length, currentTab]);

  useEffect(() => {
    if (visibleTabIndices.length === 0) return;

    const params = new URLSearchParams(window.location.search);
    const tab = (params.get('tab') || '').toLowerCase();
    const targetRealTab =
      tab === 'account-settings' || tab === 'settings' ? 1 :
      tab === 'your-profiles' || tab === 'profiles' ? 0 :
      tab === 'additional-members' || tab === 'minor-access' ? 2 :
      -1;

    if (targetRealTab < 0) return;
    const displayIndex = visibleTabIndices.indexOf(targetRealTab);
    if (displayIndex >= 0) {
      setCurrentTab(displayIndex);
    }
  }, [visibleTabIndices]);

  useEffect(() => {
    setResolvedIsBusinessAccount(isBusinessAccount);
  }, [isBusinessAccount]);

  useEffect(() => {
    const loadResolvedBusinessFlag = async () => {
      if (isJointContext) return;
      try {
        const details = await userAccountService.getAccountDetails();
        if ('isJointContext' in details) return;
        if (details.isBusinessAccount) {
          setResolvedIsBusinessAccount(true);
        }
      } catch (err) {
        console.error('Failed to resolve business account flag from account details:', err);
      }
    };
    loadResolvedBusinessFlag();
  }, [isJointContext]);

  const [viewContentTab, setViewContentTab] = useState(0); // 0=Personal, 1=Professional, 2=Social, 3=Interests
  const [selectedAlias, setSelectedAlias] = useState<ProfileAliasDto | null>(null);
  const [profile, setProfile] = useState<ProfileDto | null>(null);
  const [aliases, setAliases] = useState<ProfileAliasDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [_isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [manageAliasOpen, setManageAliasOpen] = useState(false);
  
  // Business member aliases — shown in "Your Profiles" tab for business accounts
  const [businessMemberAliases, setBusinessMemberAliases] = useState<JointAccountAlias[]>([]);

  // Business-member alias state (populated when isJointContext is true)
  const [aliasDetails, setAliasDetails] = useState<AliasAccountDetails | null>(null);
  const [aliasDisplayName, setAliasDisplayName] = useState('');
  const [savingDisplayName, setSavingDisplayName] = useState(false);
  const [aliasDisplayNameSuccess, setAliasDisplayNameSuccess] = useState(false);

  // Referral state
  const [referralData, setReferralData] = useState<ReferralLinksResponse | null>(null);
  const [referralSchedule, setReferralSchedule] = useState<ReferralSchedule | null>(null);
  const [copiedCode, setCopiedCode] = useState(false);
  const [copiedPartnerId, setCopiedPartnerId] = useState<string | null>(null);
  const [expandedPrograms, setExpandedPrograms] = useState<Set<string>>(new Set());
  const [referralsDialogOpen, setReferralsDialogOpen] = useState(false);
  const [referralsDialogTier, setReferralsDialogTier] = useState<number | null>(null);
  const [referralsList, setReferralsList] = useState<ReferredUser[]>([]);
  const [loadingReferrals, setLoadingReferrals] = useState(false);

  useEffect(() => {
    if (isJointContext) {
      userAccountService.getAccountDetails().then(details => {
        if ('isJointContext' in details) {
          setAliasDetails(details as AliasAccountDetails);
          setAliasDisplayName((details as AliasAccountDetails).displayName ?? '');
        }
      }).catch(err => console.error('Failed to load alias details:', err));
    } else {
      loadAliases();
      loadReferralData();
      if (resolvedIsBusinessAccount) {
        loadBusinessMemberAliases();
      }
    }
  }, [isJointContext, resolvedIsBusinessAccount]);
  
  const loadReferralData = async () => {
    try {
      const [links, schedule] = await Promise.all([
        userAccountService.getReferralLinks(),
        userAccountService.getReferralSchedule()
      ]);
      setReferralData(links);
      setReferralSchedule(schedule);
    } catch (err) {
      console.log('Error loading referral data:', err);
    }
  };
  
  const handleCopyReferralCode = async () => {
    if (!referralData?.usernameReferralUrl) return;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(referralData.usernameReferralUrl);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = referralData.usernameReferralUrl;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    } catch (err) {
      window.prompt('Copy this link:', referralData.usernameReferralUrl);
    }
  };

  const handleCopyPartnerCode = async (url: string, codeId: string) => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = url;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      setCopiedPartnerId(codeId);
      setTimeout(() => setCopiedPartnerId(null), 2000);
    } catch (err) {
      window.prompt('Copy this link:', url);
    }
  };

  const toggleProgramExpand = (codeId: string) => {
    setExpandedPrograms(prev => {
      const next = new Set(prev);
      if (next.has(codeId)) {
        next.delete(codeId);
      } else {
        next.add(codeId);
      }
      return next;
    });
  };
  
  const handleOpenReferralsDialog = async (tier: number | null, partnerCodeId?: string) => {
    setReferralsDialogTier(tier);
    setReferralsDialogOpen(true);
    setLoadingReferrals(true);
    try {
      const list = await userAccountService.getReferrals(tier ?? undefined, partnerCodeId);
      setReferralsList(list);
    } catch (err) {
      console.error('Error loading referrals:', err);
      setReferralsList([]);
    } finally {
      setLoadingReferrals(false);
    }
  };

  useEffect(() => {
    if (selectedAlias) {
      loadProfile();
    }
  }, [selectedAlias]);

  const loadAliases = async () => {
    try {
      setError(null);
      const data = await socialService.getMyAliases();
      setAliases(data);
      
      if (data.length > 0) {
        const primary = data.find(a => a.bitIsPrimary) || data[0];
        setSelectedAlias((current) => {
          if (!current) return primary;
          const stillExists = data.some((a) => a.idAliasID === current.idAliasID);
          return stillExists ? current : primary;
        });
      } else {
        setLoading(false);
        setSelectedAlias(null);
        setError('No profiles found. Create your first profile.');
      }
    } catch (error: any) {
      console.error('Failed to load aliases:', error);
      setLoading(false);
      setError('Unable to connect to Social Profiles API. Please ensure the MailBox.API is running.');
    }
  };

  const loadProfile = async () => {
    if (!selectedAlias) return;
    
    try {
      setLoading(true);
      setError(null);
      const data = await socialService.getProfileByAliasId(selectedAlias.idAliasID);
      if (data) {
        setProfile(data);
      } else {
        // No profile exists yet - create an empty profile for editing
        setProfile({
          idProfileID: '',
          idAliasID: selectedAlias.idAliasID,
          chrAliasName: selectedAlias.chrAliasName,
          chrDisplayName: selectedAlias.chrDisplayName || selectedAlias.chrAliasName,
          txtBio: '',
          chrProfileImageURL: '',
          chrCoverImageURL: '',
          chrLocation: '',
          chrWebsiteURL: '',
          intVisibilityLevel: 1,
          chrJobTitle: '',
          chrCompany: '',
          txtSkills: '',
          chrEducation: '',
          txtProfessionalBio: '',
          socialLinks: [],
          interests: []
        });
        // Automatically open in edit mode for new profiles
        setIsEditing(true);
      }
    } catch (error: any) {
      console.error('Failed to load profile:', error);
      setError('Failed to load profile data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadBusinessMemberAliases = async () => {
    try {
      const accounts = await jointAccountService.getMyAccounts();
      const business = accounts?.find(
        a => a.accountType === 'BUSINESS' && (a.userRole === 'PRIMARY' || a.userRole === 'ADMIN')
      );
      if (business) {
        const aliases = await jointAccountService.getAliases(business.jointAccountId);
        setBusinessMemberAliases((aliases || []).filter(a => !a.isMinor));
      }
    } catch (err) {
      console.error('Failed to load business member aliases:', err);
    }
  };

  const handleSaveAliasDisplayName = async () => {
    setSavingDisplayName(true);
    try {
      await userAccountService.updateAliasDisplayName(aliasDisplayName);
      setAliasDisplayNameSuccess(true);
      setTimeout(() => setAliasDisplayNameSuccess(false), 3000);
    } catch (err) {
      console.error('Failed to update display name:', err);
    } finally {
      setSavingDisplayName(false);
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
    setIsEditing(false);
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSave = async () => {
    await loadProfile(); // Refresh so view shows updated data
    setIsEditing(false); // Then switch to view (same tab is already in viewContentTab)
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const handleAliasRefresh = async () => {
    try {
      const data = await socialService.getMyAliases();
      setAliases(data);
      if (data.length > 0) {
        const primary = data.find((a) => a.bitIsPrimary) || data[0];
        let nextSelected = primary;
        setSelectedAlias((current) => {
          if (!current) return primary;
          const stillExists = data.some((a) => a.idAliasID === current.idAliasID);
          nextSelected = stillExists ? data.find((a) => a.idAliasID === current.idAliasID)! : primary;
          return nextSelected;
        });
        const profileData = await socialService.getProfileByAliasId(nextSelected.idAliasID);
        setProfile(profileData);
      } else {
        setSelectedAlias(null);
        setLoading(false);
        setError('No profiles found. Create your first profile.');
      }
    } catch (err: any) {
      console.error('Failed to refresh aliases:', err);
    }
  };

  const manageAliasDrawer = (
    <Drawer
      anchor="top"
      open={manageAliasOpen}
      onClose={() => setManageAliasOpen(false)}
      sx={{ '& .MuiDrawer-paper': { height: '80vh', maxHeight: '80vh' } }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="h6">Manage Aliases</Typography>
        <IconButton onClick={() => setManageAliasOpen(false)} aria-label="Close">
          <CloseIcon />
        </IconButton>
      </Box>
      <Box sx={{ overflow: 'auto', flex: 1, p: 2 }}>
        <AliasManager
          aliases={aliases}
          onRefresh={handleAliasRefresh}
          onClose={() => setManageAliasOpen(false)}
        />
      </Box>
    </Drawer>
  );

  // When logged in as a business-member alias, show a dedicated business member profile view
  if (isJointContext) {
    if (!aliasDetails) {
      return (
        <Container maxWidth="md" sx={{ py: 4 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <CircularProgress />
            <Typography>Loading business member profile...</Typography>
          </Box>
        </Container>
      );
    }
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <BusinessIcon color="primary" sx={{ fontSize: 40 }} />
            <Box>
              <Typography variant="h5" fontWeight="bold">
                {aliasDetails.aliasUsername}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Member of <strong>{aliasDetails.jointAccountName}</strong> &bull; {aliasDetails.role}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>Display Name</Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Box
                component="input"
                value={aliasDisplayName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAliasDisplayName(e.target.value)}
                placeholder="Enter your display name"
                sx={{
                  flex: 1, p: 1, border: '1px solid', borderColor: 'divider',
                  borderRadius: 1, fontSize: '0.875rem', fontFamily: 'inherit',
                  '&:focus': { outline: 'none', borderColor: 'primary.main' }
                }}
              />
              <Button
                variant="contained"
                size="small"
                onClick={handleSaveAliasDisplayName}
                disabled={savingDisplayName}
              >
                {savingDisplayName ? 'Saving…' : 'Save'}
              </Button>
            </Box>
            {aliasDisplayNameSuccess && (
              <Typography variant="caption" color="success.main" sx={{ mt: 0.5, display: 'block' }}>
                Display name updated.
              </Typography>
            )}
          </Box>

          <Typography variant="subtitle2" gutterBottom>Permissions</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {aliasDetails.canViewBalance && <Chip size="small" label="View Balance" color="info" variant="outlined" />}
            {aliasDetails.canViewHistory && <Chip size="small" label="View History" color="info" variant="outlined" />}
            {aliasDetails.canSpend && <Chip size="small" label="Send Messages" color="success" variant="outlined" />}
            {aliasDetails.canDeposit && <Chip size="small" label="Add Funds" color="success" variant="outlined" />}
            {aliasDetails.canWithdraw && <Chip size="small" label="Withdraw" color="success" variant="outlined" />}
            {aliasDetails.canInviteMembers && <Chip size="small" label="Invite Members" color="warning" variant="outlined" />}
            {aliasDetails.canEditSettings && <Chip size="small" label="Edit Settings" color="warning" variant="outlined" />}
          </Box>

          {aliasDetails.allowedTabs.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>Accessible Tabs</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {aliasDetails.allowedTabs.map(tab => (
                  <Chip key={tab} size="small" label={tab} variant="outlined" />
                ))}
              </Box>
            </Box>
          )}
        </Paper>

        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
          This is your business member profile for <strong>{aliasDetails.jointAccountName}</strong>.
          Messages you send and receive use the shared <strong>{aliasDetails.jointAccountName}</strong> account balance.
        </Typography>

        <ChangePassword
          open={changePasswordOpen}
          onClose={() => setChangePasswordOpen(false)}
        />
      </Container>
    );
  }

  if (loading && !profile && !error) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress />
          <Typography>Loading your profile...</Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <>
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert 
          severity="error" 
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={loadAliases}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
        
        {error.includes('Social Profiles API') && (
          <Paper sx={{ p: 3, bgcolor: 'info.light' }}>
            <Typography variant="h6" gutterBottom>
              ðŸš€ Start the MailBox API
            </Typography>
            <Typography variant="body2" paragraph>
              The Social Profiles feature requires the MailBox.API to be running.
            </Typography>
            <Typography variant="body2" component="div">
              <strong>Option 1: Using Batch File</strong>
              <ol>
                <li>Run <code>START_SERVERS.bat</code> from the project root</li>
                <li>Wait for "Startup Complete!" message</li>
                <li>Click "Retry" above</li>
              </ol>
            </Typography>
            <Typography variant="body2" component="div" sx={{ mt: 2 }}>
              <strong>Option 2: Manual Start</strong>
              <ol>
                <li>Open a terminal</li>
                <li>cd MailBox.API</li>
                <li>dotnet run --urls http://localhost:5220</li>
                <li>Wait for "Now listening on: http://localhost:5220"</li>
                <li>Click "Retry" above</li>
              </ol>
            </Typography>
          </Paper>
        )}
        
        {aliases.length === 0 && !error.includes('Social Profiles API') && (
          <Box sx={{ mt: 3 }}>
            <Button variant="contained" onClick={() => setManageAliasOpen(true)}>
              Manage Aliases
            </Button>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Create your first profile to get started.
            </Typography>
          </Box>
        )}
      </Container>
      {manageAliasDrawer}
      </>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header with Referral Section */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h4">
            My Profile
          </Typography>
          
          {/* Referral Section - visible from all tabs */}
          {referralData && (
            <Box sx={{ 
              backgroundColor: 'background.paper', 
              border: 1, 
              borderColor: 'divider',
              borderRadius: 2, 
              p: 2,
              maxWidth: 700,
              flex: '1 1 auto'
            }}>
              <Typography variant="subtitle1" fontWeight="bold" color="text.primary" sx={{ mb: 1 }}>
                Refer & Earn
              </Typography>
              {/* Username Referral Code Row */}
              <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" sx={{ mb: 1 }}>
                <Typography variant="body2" fontWeight="bold" color="text.primary">
                  Your Referral Code:
                </Typography>
                <Tooltip title={copiedCode ? 'Copied!' : `Click to copy: ${referralData.usernameReferralUrl}`}>
                  <Chip
                    label={referralData.usernameReferralCode}
                    size="small"
                    color="primary"
                    onClick={handleCopyReferralCode}
                    icon={copiedCode ? <CheckCircleIcon /> : <ContentCopyIcon />}
                    sx={{ cursor: 'pointer' }}
                  />
                </Tooltip>
                
                {/* Tier Referral Links */}
                {referralSchedule && referralSchedule.maxTiers >= 1 && (
                  <Link
                    component="button"
                    variant="body2"
                    onClick={() => handleOpenReferralsDialog(1)}
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                  >
                    <PeopleIcon fontSize="small" />
                    Tier 1
                  </Link>
                )}
                {referralSchedule && referralSchedule.maxTiers >= 2 && (
                  <Link
                    component="button"
                    variant="body2"
                    onClick={() => handleOpenReferralsDialog(2)}
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                  >
                    <PeopleIcon fontSize="small" />
                    Tier 2
                  </Link>
                )}
                {referralSchedule && referralSchedule.maxTiers >= 3 && (
                  <Link
                    component="button"
                    variant="body2"
                    onClick={() => handleOpenReferralsDialog(3)}
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                  >
                    <PeopleIcon fontSize="small" />
                    Tier 3
                  </Link>
                )}
              </Stack>
              
              {/* Inline Referral Schedule Description (for non-partners or when no partner codes) */}
              {referralSchedule && (!referralData.partnerCodes || referralData.partnerCodes.length === 0) && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    <strong>{referralSchedule.scheduleName}</strong>
                    {referralSchedule.description && ` - ${referralSchedule.description}`}
                  </Typography>
                  {referralSchedule.maxTiers > 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      {referralSchedule.tier1Percentage > 0 && (
                        <>Tier 1: {referralSchedule.tier1Percentage}%{referralSchedule.tier1MaxPayout && ` (max $${referralSchedule.tier1MaxPayout})`}{referralSchedule.tier1DurationMonths && ` for ${referralSchedule.tier1DurationMonths}mo`}</>
                      )}
                      {referralSchedule.maxTiers >= 2 && referralSchedule.tier2Percentage > 0 && (
                        <> | Tier 2: {referralSchedule.tier2Percentage}%{referralSchedule.tier2MaxPayout && ` (max $${referralSchedule.tier2MaxPayout})`}</>
                      )}
                      {referralSchedule.maxTiers >= 3 && referralSchedule.tier3Percentage > 0 && (
                        <> | Tier 3: {referralSchedule.tier3Percentage}%{referralSchedule.tier3MaxPayout && ` (max $${referralSchedule.tier3MaxPayout})`}</>
                      )}
                    </Typography>
                  )}
                </Box>
              )}

              {/* Partner Referral Codes Table - shown when user has multiple referral codes */}
              {referralData.partnerCodes && referralData.partnerCodes.length > 0 && (
                <Box sx={{ mt: 1.5 }}>
                  <Typography variant="body2" fontWeight="bold" color="text.primary" sx={{ mb: 1 }}>
                    Your Referral Programs:
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold', py: 0.75, width: 32 }} />
                          <TableCell sx={{ fontWeight: 'bold', py: 0.75 }}>Program</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', py: 0.75 }}>Code</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold', py: 0.75 }}>Tiers</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold', py: 0.75 }}>Sign-ups</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {referralData.partnerCodes.map((code) => {
                          const isExpanded = expandedPrograms.has(code.partnerCodeId);
                          return (
                            <React.Fragment key={code.partnerCodeId}>
                              {/* Main program row */}
                              <TableRow 
                                hover 
                                onClick={() => toggleProgramExpand(code.partnerCodeId)}
                                sx={{ cursor: 'pointer', '& > *': { borderBottom: isExpanded ? 'unset' : undefined } }}
                              >
                                <TableCell sx={{ py: 0.5, width: 32 }}>
                                  <IconButton size="small">
                                    {isExpanded ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                                  </IconButton>
                                </TableCell>
                                <TableCell sx={{ py: 0.75 }}>
                                  <Typography variant="body2" fontWeight="medium">
                                    {code.displayName || code.scheduleName}
                                  </Typography>
                                </TableCell>
                                <TableCell sx={{ py: 0.75 }}>
                                  <Tooltip title={copiedPartnerId === code.partnerCodeId ? 'Copied!' : 'Click to copy referral link'}>
                                    <Chip
                                      label={code.referralCode}
                                      size="small"
                                      color={copiedPartnerId === code.partnerCodeId ? 'success' : 'secondary'}
                                      variant="outlined"
                                      onClick={(e) => { e.stopPropagation(); handleCopyPartnerCode(code.referralUrl, code.partnerCodeId); }}
                                      icon={copiedPartnerId === code.partnerCodeId ? <CheckCircleIcon /> : <ContentCopyIcon />}
                                      sx={{ cursor: 'pointer', fontFamily: 'monospace', fontSize: '0.75rem' }}
                                    />
                                  </Tooltip>
                                </TableCell>
                                <TableCell align="center" sx={{ py: 0.75 }}>
                                  <Chip label={`${code.maxTiers}-tier`} size="small" variant="outlined" color="info" />
                                </TableCell>
                                <TableCell align="center" sx={{ py: 0.75 }}>
                                  <Chip
                                    label={
                                      code.maxTiers === 1
                                        ? String(code.tier1Count)
                                        : code.maxTiers === 2
                                        ? `${code.tier1Count} / ${code.tier2Count}`
                                        : `${code.tier1Count} / ${code.tier2Count} / ${code.tier3Count}`
                                    }
                                    size="small"
                                    color={(code.tier1Count + code.tier2Count + code.tier3Count) > 0 ? 'success' : 'default'}
                                    variant="outlined"
                                    title={`Tier 1: ${code.tier1Count} direct sign-ups via this code`}
                                  />
                                </TableCell>
                              </TableRow>

                              {/* Expandable tier details row */}
                              <TableRow>
                                <TableCell sx={{ py: 0, px: 0 }} colSpan={5}>
                                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                    <Box sx={{ px: 2, py: 1.5, bgcolor: 'grey.50' }}>
                                      {/* Description */}
                                      {code.scheduleDescription && (
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                                          {code.scheduleDescription}
                                        </Typography>
                                      )}

                                      {/* Share Links */}
                                      <Box sx={{ mb: 2 }}>
                                        <Typography variant="caption" color="text.secondary" fontWeight="medium" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                          Share Links
                                        </Typography>
                                        <Stack spacing={0.75} sx={{ mt: 0.75 }}>
                                          {/* Identified link — shows the partner's username in the URL */}
                                          <Stack direction="row" alignItems="center" spacing={1}>
                                            <Typography variant="caption" color="text.secondary" sx={{ width: 80, flexShrink: 0 }}>
                                              Identified
                                            </Typography>
                                            <Chip
                                              label={code.referralUrl}
                                              size="small"
                                              variant="outlined"
                                              color={copiedPartnerId === code.partnerCodeId ? 'success' : 'default'}
                                              icon={copiedPartnerId === code.partnerCodeId ? <CheckCircleIcon /> : <ContentCopyIcon />}
                                              onClick={(e) => { e.stopPropagation(); handleCopyPartnerCode(code.referralUrl, code.partnerCodeId); }}
                                              sx={{ cursor: 'pointer', fontFamily: 'monospace', fontSize: '0.7rem', maxWidth: '100%' }}
                                            />
                                          </Stack>
                                          {/* Anonymous link — short opaque code, partner identity hidden */}
                                          <Stack direction="row" alignItems="center" spacing={1}>
                                            <Typography variant="caption" color="text.secondary" sx={{ width: 80, flexShrink: 0 }}>
                                              Anonymous
                                            </Typography>
                                            <Chip
                                              label={code.shortReferralUrl}
                                              size="small"
                                              variant="outlined"
                                              color={copiedPartnerId === `${code.partnerCodeId}-anon` ? 'success' : 'default'}
                                              icon={copiedPartnerId === `${code.partnerCodeId}-anon` ? <CheckCircleIcon /> : <ContentCopyIcon />}
                                              onClick={(e) => { e.stopPropagation(); handleCopyPartnerCode(code.shortReferralUrl, `${code.partnerCodeId}-anon`); }}
                                              sx={{ cursor: 'pointer', fontFamily: 'monospace', fontSize: '0.7rem', maxWidth: '100%' }}
                                            />
                                          </Stack>
                                        </Stack>
                                      </Box>

                                      {/* Tier details grid */}
                                      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                                        {code.tier1Percentage > 0 && (
                                          <Paper variant="outlined" sx={{ p: 1.5, minWidth: 160, flex: '1 1 auto' }}>
                                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                                              <Typography variant="subtitle2" color="primary.main">
                                                Tier 1 (Direct)
                                              </Typography>
                                              <Link
                                                component="button"
                                                variant="caption"
                                                onClick={() => handleOpenReferralsDialog(1, code.partnerCodeId)}
                                                sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                                              >
                                                <PeopleIcon sx={{ fontSize: 14 }} />
                                                View ({code.tier1Count})
                                              </Link>
                                            </Stack>
                                            <Typography variant="body2">
                                              <strong>{code.tier1Percentage}%</strong> of ad revenue
                                            </Typography>
                                            {code.tier1MaxPayout != null && (
                                              <Typography variant="caption" color="text.secondary" display="block">
                                                Max payout: ${code.tier1MaxPayout}
                                              </Typography>
                                            )}
                                            {code.tier1DurationMonths != null && (
                                              <Typography variant="caption" color="text.secondary" display="block">
                                                Duration: {code.tier1DurationMonths} months
                                              </Typography>
                                            )}
                                          </Paper>
                                        )}
                                        
                                        {code.maxTiers >= 2 && code.tier2Percentage > 0 && (
                                          <Paper variant="outlined" sx={{ p: 1.5, minWidth: 160, flex: '1 1 auto' }}>
                                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                                              <Typography variant="subtitle2" color="primary.main">
                                                Tier 2 (Indirect)
                                              </Typography>
                                              <Link
                                                component="button"
                                                variant="caption"
                                                onClick={() => handleOpenReferralsDialog(2, code.partnerCodeId)}
                                                sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                                              >
                                                <PeopleIcon sx={{ fontSize: 14 }} />
                                                View ({code.tier2Count})
                                              </Link>
                                            </Stack>
                                            <Typography variant="body2">
                                              <strong>{code.tier2Percentage}%</strong> of ad revenue
                                            </Typography>
                                            {code.tier2MaxPayout != null && (
                                              <Typography variant="caption" color="text.secondary" display="block">
                                                Max payout: ${code.tier2MaxPayout}
                                              </Typography>
                                            )}
                                            {code.tier2DurationMonths != null && (
                                              <Typography variant="caption" color="text.secondary" display="block">
                                                Duration: {code.tier2DurationMonths} months
                                              </Typography>
                                            )}
                                          </Paper>
                                        )}
                                        
                                        {code.maxTiers >= 3 && code.tier3Percentage > 0 && (
                                          <Paper variant="outlined" sx={{ p: 1.5, minWidth: 160, flex: '1 1 auto' }}>
                                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                                              <Typography variant="subtitle2" color="primary.main">
                                                Tier 3 (2nd Indirect)
                                              </Typography>
                                              <Link
                                                component="button"
                                                variant="caption"
                                                onClick={() => handleOpenReferralsDialog(3, code.partnerCodeId)}
                                                sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                                              >
                                                <PeopleIcon sx={{ fontSize: 14 }} />
                                                View ({code.tier3Count})
                                              </Link>
                                            </Stack>
                                            <Typography variant="body2">
                                              <strong>{code.tier3Percentage}%</strong> of ad revenue
                                            </Typography>
                                            {code.tier3MaxPayout != null && (
                                              <Typography variant="caption" color="text.secondary" display="block">
                                                Max payout: ${code.tier3MaxPayout}
                                              </Typography>
                                            )}
                                            {code.tier3DurationMonths != null && (
                                              <Typography variant="caption" color="text.secondary" display="block">
                                                Duration: {code.tier3DurationMonths} months
                                              </Typography>
                                            )}
                                          </Paper>
                                        )}
                                      </Stack>
                                    </Box>
                                  </Collapse>
                                </TableCell>
                              </TableRow>
                            </React.Fragment>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>

      {visibleTabIndices.length > 0 && (
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={currentTab} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
          {visibleTabIndices.map((realIndex) => (
            <Tab
              key={realIndex}
              label={tabLabels[realIndex]}
              icon={realIndex === 1 ? <SettingsIcon fontSize="small" /> : realIndex === 2 ? (resolvedIsBusinessAccount ? <BusinessIcon fontSize="small" /> : <FamilyIcon fontSize="small" />) : undefined}
              iconPosition={realIndex > 0 ? 'start' : undefined}
            />
          ))}
        </Tabs>
      </Box>
      )}

      {visibleTabIndices.map((realIndex, displayIndex) => (
        <TabPanel key={realIndex} value={currentTab} index={displayIndex}>
          {realIndex === 0 && profile && selectedAlias && (
            <>
              {!_isEditing ? (
                <>
                <ProfileView
                  profile={profile}
                  isOwner={true}
                  onEdit={handleEdit}
                  contentTabValue={viewContentTab}
                  onContentTabChange={setViewContentTab}
                  aliases={aliases}
                  selectedAliasId={selectedAlias.idAliasID}
                  onSelectAlias={setSelectedAlias}
                  onOpenManageAliases={resolvedIsBusinessAccount ? undefined : () => setManageAliasOpen(true)}
                  selectedAliasType={selectedAlias.intAliasType}
                />
                {resolvedIsBusinessAccount && businessMemberAliases.length > 0 && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="h6" sx={{ mb: 1.5 }}>Team Member Profiles</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                      {businessMemberAliases.map(alias => (
                        <Paper
                          key={alias.aliasId}
                          component="a"
                          href={`/profile/${alias.aliasUsername}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          elevation={1}
                          sx={{
                            p: 2, minWidth: 180, display: 'flex', flexDirection: 'column',
                            gap: 0.5, textDecoration: 'none', cursor: 'pointer',
                            '&:hover': { boxShadow: 4 }
                          }}
                        >
                          <Typography fontWeight="bold" variant="body1">
                            {alias.displayName || alias.aliasUsername}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            @{alias.aliasUsername}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {alias.role}
                          </Typography>
                        </Paper>
                      ))}
                    </Box>
                  </Box>
                )}
                </>
              
              ) : (
                <EnhancedProfileEditor
                  profile={profile}
                  onSave={handleSave}
                  onCancel={handleCancel}
                  initialTab={viewContentTab}
                  onTabChange={setViewContentTab}
                />
              )}
            </>
          )}
          {realIndex === 1 && (
            <AccountSettings onChangePasswordClick={() => setChangePasswordOpen(true)} />
          )}
          {realIndex === 2 && (resolvedIsBusinessAccount ? <AdditionalMembersManager /> : <MinorAccessManager />)}
        </TabPanel>
      ))}

      {visibleTabIndices.length === 0 && (
        <Typography color="text.secondary" sx={{ py: 2 }}>
          No sections are currently visible for this page.
        </Typography>
      )}

      {manageAliasDrawer}

      {/* Change Password Dialog */}
      <ChangePassword
        open={changePasswordOpen}
        onClose={() => setChangePasswordOpen(false)}
      />
      
      {/* Referrals List Dialog */}
      <Dialog 
        open={referralsDialogOpen} 
        onClose={() => setReferralsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {referralsDialogTier 
            ? `Tier ${referralsDialogTier} Referrals` 
            : 'All Referrals'}
        </DialogTitle>
        <DialogContent>
          {loadingReferrals ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : referralsList.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 2 }}>
              No referrals found{referralsDialogTier ? ` for Tier ${referralsDialogTier}` : ''}.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Username</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Joined</TableCell>
                    {!referralsDialogTier && <TableCell>Tier</TableCell>}
                    <TableCell align="right">Earnings</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {referralsList.map((referral) => (
                    <TableRow key={referral.referredUserID}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {referral.referredUsername}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {referral.referredFullName || '-'}
                      </TableCell>
                      <TableCell>
                        {fmtDate(referral.accountCreatedDate)}
                      </TableCell>
                      {!referralsDialogTier && (
                        <TableCell>
                          <Chip 
                            label={`Tier ${referral.tierLevel}`} 
                            size="small" 
                            color={referral.tierLevel === 1 ? 'primary' : referral.tierLevel === 2 ? 'secondary' : 'default'}
                            variant="outlined"
                          />
                        </TableCell>
                      )}
                      <TableCell align="right">
                        ${referral.cumulativePayout.toFixed(2)}
                        {referral.maxPayoutReached && (
                          <Chip label="Max" size="small" sx={{ ml: 1 }} />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReferralsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default MyProfile;


